import {
  Badge,
  Button,
  Col,
  Input,
  ModalProps,
  Row,
  Tooltip,
  message,
} from "antd";
import { FC, useMemo } from "react";
import * as Styled from "./EditAssetClass.css";
import { Flex } from "src/components/Flex";
import Text from "antd/lib/typography/Text";
import { PieChart, PieColors } from "../PieChart";
import { AlertCircleIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAssetClass,
  selectAssetDescription,
  selectAssetName,
  setAssetDescription,
  setAssetName,
  updateAssetItem,
} from "src/store/Goal-Based/AssetClassSlice";
import { useEditAssetClassesMutation } from "src/store/Goal-Based/goalBasedApi";
import TextArea from "antd/lib/input/TextArea";
import { theme } from "src/theme/theme";
import { colors } from "src/theme/colors";
import { line, radii } from "src/theme/ui";
import styled from "styled-components";
import { Button as UIButton, DialogActions } from "src/components/ui";

export interface IEditAssetClass {
  setVisible: (boolean) => void;
  smartfolioId: string;
  planId: number;
  setIsAssetClassSet: (boolean) => void;
}

/**
 * A pane inside a dialog.
 *
 * White with a hairline, not a periwinkle fill — on this tenant primary[10]
 * is a full brand colour rather than a tint, so two of these side by side
 * made the dialog's content read as two coloured slabs with the fields
 * floating on them. Shared by the asset-class editor and the smartfolio
 * builder, which is why it lives here and not in either.
 */
export const subCardStyle = {
  backgroundColor: colors.white,
  border: `1px solid ${line.hairline}`,
  borderRadius: `${radii.lg}px`,
  padding: 16,
};

message.config({ duration: 6 });

export const EditAssetClass: FC<ModalProps & IEditAssetClass> = ({
  setVisible,
  smartfolioId,
  setIsAssetClassSet,
  planId,
  ...rest
}) => {
  const data = useSelector(selectAssetClass) ?? [];
  const name = useSelector(selectAssetName);
  /* Read here, not inside the JSX. Two of these were called as the value of
     an attribute — legal, since they are unconditional, but a hook whose
     call site is a prop is a hook nobody notices when the branch above it
     changes. */
  const description = useSelector(selectAssetDescription);
  const [editAssetClass, { isLoading }] = useEditAssetClassesMutation();
  const scaledWeightsSum = useMemo(() => {
    return data.reduce((acc, curr) => acc + curr.weight * 1000, 0);
  }, [data]);
  const buttons = [
    {
      title: "Cancel",
      handler: () => {
        setVisible(false);
        setIsAssetClassSet(false);
      },
    },
    {
      title: "Save as a new smartfolio",
      handler: async () => {
        const payload = {
          planId,
          display_name: name,
          asset_class_items: data.map(
            ({ weight, minWeight, maxWeight, id }) => ({
              weight,
              asset_class_id: id,
              maximum_bound_weight: maxWeight,
              minimum_bound_weight: minWeight,
            }),
          ),
        };
        try {
          await editAssetClass({ payload, smartfolioId }).unwrap();
          setIsAssetClassSet(false);
          message.success(
            "You have successfully edited asset class allocations for the suggested smartfolio!",
          );
        } catch (error) {
          message.error("Failed to edit asset class allocations!");
        }
        setVisible(false);
      },
      primary: true,
    },
  ];

  const dispatch = useDispatch<any>();
  /* The two facts the dialog needs about the weights, worked out once: is it
     balanced, and by how much is it out. A reader who can see 80% in the
     total already knows the rule; what they do not have is the arithmetic,
     and it is the dialog that knows it. */
  const balanced = scaledWeightsSum === 1000;
  const short = Math.round((1000 - scaledWeightsSum) / 10);
  /* Why the save is refused, in the words of whichever rule is refusing it.
     Both were invisible: the button greyed out and said nothing, and the
     name rule is not written down anywhere on the dialog. */
  const nameShort = name.trim().length <= 5;
  const blocker = nameShort
    ? "Give the smartfolio a name of at least six characters to save it."
    : balanced
    ? undefined
    : short > 0
    ? `${short}% of the allocation is still unassigned.`
    : `The allocation is ${-short}% over.`;

  return (
    <Styled.Wrapper
      {...rest}
      /* The actions are the shell's footer band, AFTER the spread — the host
         passes footer={null} and the body scrolls now that the shell is
         bounded by the window, so a row at the end of the body is a row
         below the fold. */
      footer={
        <DialogActions>
          {/* The reason, beside the control it is about. A disabled button
              with nothing next to it is a dead end. */}
          {blocker ? <Styled.Blocker>{blocker}</Styled.Blocker> : null}
          {buttons.map(({ title, handler, primary }) => (
            <UIButton
              key={title}
              variant={primary ? "primary" : "secondary"}
              size="default"
              onClick={handler}
              loading={title === "Save as a new smartfolio" && isLoading}
              disabled={
                title === "Save as a new smartfolio" && (!balanced || nameShort)
              }
            >
              {title}
            </UIButton>
          ))}
        </DialogActions>
      }
    >
      <Styled.Title>Edit asset class</Styled.Title>
      <Flex flexDirection="column" width="100%">
        {/* Each pane is its own height. Stretched to the taller of the two,
            the table's pane ended in a hand's depth of blank paper under
            its own last line — the fields and the ring simply need more
            room than three weights do, and a pane that stops where its
            content stops says so. */}
        <Row gutter={[16, 16]} align="top">
          {/* Name, description and the ring. It carried `className="graph"`
              from when the column held only the chart, and that class hides
              it under 992px — so on a phone the dialog lost the NAME field
              while the save button, which refuses a name under six
              characters, stayed disabled with nothing the reader could do
              about it. */}
          <Col span={24} lg={8}>
            <Styled.Background>
              {/* Identity first, picture after. The ring used to open the
                  column, so the reader met a chart of the weights before
                  they had been told what they were naming. */}
              <Flex flexDirection="column" width="100%" gap="16px">
                <Flex flexDirection="column" width="100%" gap="6px">
                  <Styled.FieldLabel>
                    Name <b>*</b>
                  </Styled.FieldLabel>
                  <Input
                    maxLength={90}
                    style={{ borderRadius: "5px" }}
                    placeholder="Your Smartfolio Name"
                    value={name}
                    onChange={(e) => dispatch(setAssetName(e.target.value))}
                  />
                  {/* The rule, where the rule applies. It lived only in the
                      save button's disabled condition, so a reader with a
                      four-letter name had a dead button and no idea why. */}
                  {nameShort ? (
                    <Styled.Note>At least six characters.</Styled.Note>
                  ) : null}
                </Flex>
                <Flex flexDirection="column" width="100%" gap="6px">
                  <Styled.FieldLabel>
                    Description <i>optional</i>
                  </Styled.FieldLabel>
                  <TextArea
                    style={{ borderRadius: "5px" }}
                    rows={4}
                    placeholder="Your Smartfolio Description"
                    maxLength={225}
                    value={description}
                    onChange={(e) =>
                      dispatch(setAssetDescription(e.target.value))
                    }
                  />
                </Flex>
                {/* The ring carries its own box now — see PieChart. */}
                <Flex flexDirection="column" width="100%">
                  <PieChart
                    /* The share allocated so far, in the middle of the ring
                       — the same figure the total row carries, in the place
                       the eye already is. */
                    showTotal
                    legend={false}
                    data={data?.map(({ assetClass, weight }) => ({
                      key: assetClass,
                      value: +(weight * 100).toFixed(0),
                    }))}
                  />
                </Flex>
              </Flex>
            </Styled.Background>
          </Col>
          <Col span={24} lg={16}>
            <Styled.Background>
              <Flex flexDirection="column" width="100%" gap="1rem">
                <Styled.Row className="head">
                  <span>Asset class</span>
                  <span>Weight</span>
                  <span>Min</span>
                  <span>Max</span>
                </Styled.Row>
                {data?.map(
                  ({ assetClass, weight, minWeight, maxWeight }, index) => {
                    return (
                      <Styled.Row key={assetClass}>
                        <Badge
                          color={PieColors[index]}
                          text={assetClass}
                          style={{ color: colors.gray[700] }}
                        />
                        <Styled.Input
                          value={+((weight * 1000) / 10).toFixed(0)}
                          min={0}
                          max={100}
                          formatter={(value) => `${value}%`}
                          parser={(value) => value!.replace("%", "")}
                          onChange={(e) => {
                            dispatch(
                              updateAssetItem({
                                index,
                                attribute: "weight",
                                value: e,
                              }),
                            );
                          }}
                        />
                        <Styled.Input
                          value={((minWeight * 1000) / 10).toFixed(0)}
                          min={0}
                          max={(weight * 1000) / 10}
                          formatter={(value) => `${value}%`}
                          parser={(value) => value!.replace("%", "")}
                          onChange={(e) =>
                            dispatch(
                              updateAssetItem({
                                index,
                                attribute: "minWeight",
                                value: e,
                              }),
                            )
                          }
                        />
                        <Styled.Input
                          value={((maxWeight * 1000) / 10).toFixed(0)}
                          min={(weight * 1000) / 10}
                          max={100}
                          formatter={(value) => `${value}%`}
                          parser={(value) => value!.replace("%", "")}
                          onChange={(e) =>
                            dispatch(
                              updateAssetItem({
                                index,
                                attribute: "maxWeight",
                                value: e,
                              }),
                            )
                          }
                        />
                      </Styled.Row>
                    );
                  },
                )}

                {/* The sum, under what it sums, with the message about it
                      under the sum. */}
                <Styled.TotalRow>
                  <Styled.TotalLabel>Total</Styled.TotalLabel>
                  {/* A reading, not a disabled field — see the note on
                      TotalValue. */}
                  <Styled.TotalValue $ok={balanced}>
                    {scaledWeightsSum / 10}%
                  </Styled.TotalValue>
                </Styled.TotalRow>
                {balanced ? null : (
                  <Styled.Alert role="alert">
                    <HugeIcon icon={AlertCircleIcon} size={14} />
                    <Styled.AlertText>
                      {short > 0 ? (
                        <>
                          <b>{short}%</b> still to allocate. Add it to any of
                          the classes above.
                        </>
                      ) : (
                        <>
                          <b>{-short}%</b> over. Take it off one of the classes
                          above.
                        </>
                      )}
                    </Styled.AlertText>
                  </Styled.Alert>
                )}
              </Flex>
            </Styled.Background>
          </Col>
        </Row>
      </Flex>
    </Styled.Wrapper>
  );
};
